import { IQueryOptionsSort } from '@kurtosys/types/query/index.js';

import { get, isArrayLike, replacePlaceholders } from '../../common/commonUtils/index.js';
import { helpers } from '../../common/index.js';

type TSortMethod = (a: string | number, b: string | number, culture?: string) => number;

interface IApplySortOptions {
	collection: any[];
	sorts: IQueryOptionsSort | IQueryOptionsSort[];
	sorters: Record<string, TSortMethod>;
	culture?: string;
	inputs?: object;
	translationHelper?: helpers.TranslationHelper;
}

export function applySort(options: IApplySortOptions): any[] {
	const { collection, culture, inputs, translationHelper, sorters } = options;
	if (options.sorts) {
		const orders = {};
		const sorts = !isArrayLike(options.sorts) ? [options.sorts as IQueryOptionsSort] : options.sorts;
		sorts.forEach((sort, sortIndex) => {
			if (sort.order && sort.order.length > 0) {
				let order =
					sort.translateOrderValues && translationHelper && translationHelper.translate
						? sort.order.map((value) => translationHelper.translate(value))
						: sort.order;
				if (inputs) {
					/**
					 * Due to the replacePlaceholders expecting the AppParamsHelper to get the embed values
					 * and it not being available to the Queries as the app initialize the AppParamsHelper
					 * and populates the executionOptions.inputs based on that initialization,
					 * we need to create a mock AppParamsHelper with the values on the rawAppParams
					 * for use by the replace placeholders.
					 * This will maintain consistency between Fund Center and the apps.
					 * We could not update replacePlaceholders to not use the appParamsHelper as we are not
					 * sure if there are any other application that might be using this.
					 */
					const appParams = { rawAppParams: inputs } as any;
					order = order.map((value) => replacePlaceholders({ text: value, appParamsHelper: appParams }));
				}
				const key = `sort_${sortIndex}`;
				orders[key] = order;
				sort['_sortKey'] = key;
			}
		});

		/*
			collection = collection
					.map((value) => {
						const orderIndex = order.indexOf(value[sort.key]);
						value.__order =
							orderIndex > -1 ? orderIndex : order.length;
						return value;
					})
					.sort((a, b) => sorters.number(a.__order, b.__order))
					.map((value) => {
						delete value.__order;
						return value;
					});
		*/
		const getOrderValue = (sort: IQueryOptionsSort, value: any): any => {
			const order = orders[sort['_sortKey']];
			const orderIndex = order.indexOf(value[sort.key]);
			return orderIndex > -1 ? orderIndex : order.length;
		};
		const innerSort = (a: any, b: any, sort: IQueryOptionsSort): number => {
			if (sort.order && sort['_sortKey']) {
				const aOrder = getOrderValue(sort, a);
				const bOrder = getOrderValue(sort, b);
				return sorters.number(aOrder, bOrder);
			}
			const sortFunction: (a: any, b: any, culture?: string) => number = sorters[sort.type];
			if (sortFunction) {
				const aValue = get(a, sort.key);
				const bValue = get(b, sort.key);
				return sortFunction(aValue, bValue, culture);
			}
			return 0;
		};

		collection.sort((a, b) => {
			for (const sort of sorts as IQueryOptionsSort[]) {
				let result = innerSort(a, b, sort);
				if (result !== 0) {
					if (sort.direction === 'DESC') {
						result = result * -1;
					}
					return result;
				}
			}
			return 0;
		});
		sorts.forEach((sort) => {
			delete sort['_sortKey'];
		});
	}
	return collection;
}
